import { useState } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import BookingDialog from "@/components/BookingDialog";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram } from "lucide-react";
import { toast } from "sonner";

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const contactInfo = [
  {
    icon: Phone,
    label: "Telefono",
    value: "+39 351 725 2089",
    href: "tel:+393517252089",
  },
  {
    icon: Mail,
    label: "Email",
    value: "kairoslabarberia@outlook.it",
    href: "mailto:kairoslabarberia@outlook.it",
  },
  {
    icon: MapPin,
    label: "Indirizzo",
    value: "Desenzano del Garda, BS",
    href: "https://maps.google.com/?q=Desenzano+del+Garda",
  },
];

const socialLinks = [
  {
    icon: Facebook,
    label: "Facebook",
    href: "https://www.facebook.com/p/Kairos-La-Barberia-100092947623058/",
  },
  {
    icon: Instagram,
    label: "Instagram",
    href: "https://www.instagram.com/kairos_la_barberia/",
  },
  {
    icon: TikTokIcon,
    label: "TikTok",
    href: "https://www.tiktok.com/@kairoslabarberia",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: "easeOut" },
};

export const ContattiPage = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1000));

    toast.success("Candidatura inviata!", {
      description: "Ti ricontatteremo al più presto.",
    });

    setFormData({ email: "", message: "" });
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-kairos-black" data-testid="contatti-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6" data-testid="contatti-hero">
        <div className="max-w-7xl mx-auto text-center">
          <motion.p
            className="text-kairos-gold text-xs uppercase tracking-[0.3em] mb-4 font-accent"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            Contatti
          </motion.p>
          <motion.h1
            className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-6 tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Vieni a trovarmi
          </motion.h1>
          <motion.p
            className="text-white/60 max-w-xl mx-auto text-base md:text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Sono qui per te. Contattami o vieni a trovarmi in salone.
          </motion.p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 px-6" data-testid="contact-info">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contactInfo.map((info, index) => (
              <motion.a
                key={info.label}
                href={info.href}
                target={info.label === "Indirizzo" ? "_blank" : undefined}
                rel={info.label === "Indirizzo" ? "noopener noreferrer" : undefined}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="bg-kairos-gray/30 border border-white/5 p-8 text-center group hover:border-kairos-gold/30 transition-all duration-500"
                data-testid={`contact-${info.label.toLowerCase()}`}
              >
                <div className="inline-flex items-center justify-center w-14 h-14 border border-kairos-gold/30 mb-6 group-hover:bg-kairos-gold/10 transition-colors">
                  <info.icon className="w-6 h-6 text-kairos-gold" />
                </div>
                <p className="text-kairos-gold text-xs uppercase tracking-[0.2em] mb-2">
                  {info.label}
                </p>
                <p className="text-white font-medium">{info.value}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Social & Hours */}
      <section className="py-16 px-6 bg-kairos-gray/20" data-testid="social-hours">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Social */}
            <motion.div {...fadeUp}>
              <h2 className="font-serif text-2xl text-white mb-6">Seguimi</h2>
              <p className="text-white/60 mb-8">
                Resta aggiornato sulle ultime novità e tendenze. Seguimi sui social!
              </p>
              <div className="flex gap-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 border border-white/20 flex items-center justify-center text-white/60 hover:border-kairos-gold hover:text-kairos-gold transition-all duration-300"
                    data-testid={`social-${social.label.toLowerCase()}`}
                  >
                    {typeof social.icon === "function" ? (
                      <social.icon />
                    ) : (
                      <social.icon className="w-5 h-5" />
                    )}
                  </a>
                ))}
              </div>
            </motion.div>

            {/* Hours */}
            <motion.div {...fadeUp}>
              <h2 className="font-serif text-2xl text-white mb-6 flex items-center gap-3">
                <Clock className="w-6 h-6 text-kairos-gold" />
                Prenota con l'App
              </h2>
              <p className="text-white/60 mb-6">
                Scarica l'app Kairos La Barberia per prenotare il tuo appuntamento 
                in pochi tap e gestire in autonomia le tue prenotazioni.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://apps.apple.com/it/app/kairos-la-barberia/id6449093373"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-white text-kairos-black px-6 py-3 hover:bg-white/90 transition-colors"
                  data-testid="contact-app-apple"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  App Store
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=app.kairos.working&hl=it"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-kairos-gold text-kairos-black px-6 py-3 hover:bg-kairos-gold-light transition-colors"
                  data-testid="contact-app-android"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                  </svg>
                  Google Play
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Work With Us */}
      <section className="py-24 px-6" data-testid="work-with-us">
        <div className="max-w-2xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-kairos-gold text-xs uppercase tracking-[0.3em] mb-4 font-accent">
              Lavora con me
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-white tracking-tight">
              Unisciti al team
            </h2>
            <p className="text-white/60 mt-4">
              Sei un barbiere appassionato? Inviami la tua candidatura.
            </p>
          </motion.div>

          <motion.form
            {...fadeUp}
            onSubmit={handleSubmit}
            className="space-y-6"
            data-testid="contact-form"
          >
            <div>
              <label className="block text-white/60 text-sm mb-2">Email *</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-kairos-gray/50 border border-white/10 px-4 py-3 text-white focus:border-kairos-gold focus:outline-none transition-colors"
                placeholder="La tua email"
                data-testid="form-email"
              />
            </div>
            <div>
              <label className="block text-white/60 text-sm mb-2">Perché proprio tu?</label>
              <textarea
                rows={5}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full bg-kairos-gray/50 border border-white/10 px-4 py-3 text-white focus:border-kairos-gold focus:outline-none transition-colors resize-none"
                placeholder="Raccontaci di te..."
                data-testid="form-message"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-kairos-gold text-kairos-black py-4 text-sm uppercase tracking-[0.2em] font-bold hover:bg-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="form-submit"
            >
              {isSubmitting ? "Invio in corso..." : "Invia la tua candidatura"}
            </button>
          </motion.form>
        </div>
      </section>

      {/* Book CTA */}
      <section className="py-24 px-6 bg-kairos-gray/20" data-testid="contatti-cta">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-6 tracking-tight">
              Pronto per il tuo appuntamento?
            </h2>
            <p className="text-white/60 max-w-xl mx-auto mb-10">
              Prenota ora e vivi l'esperienza Kairos.
            </p>
            <button
              onClick={() => setBookingOpen(true)}
              className="bg-kairos-gold text-kairos-black px-12 py-5 text-sm uppercase tracking-[0.2em] font-bold hover:bg-white transition-all duration-300"
              data-testid="contatti-book-btn"
            >
              Prenota Ora
            </button>
          </motion.div>
        </div>
      </section>

      <Footer />
      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} />
    </div>
  );
};

export default ContattiPage;
